import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronLeft, ChevronRight, Lock, User, RefreshCw, AlertCircle } from 'lucide-react';

interface Props {
  businessId: string;
  onUpdate?: () => void;
  onAiSync?: () => void;
  aiLoading?: boolean;
}

const AvailabilityCalendar = ({ businessId, onUpdate, onAiSync, aiLoading }: Props) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [rooms, setRooms] = useState<any[]>([]);

  // 1. Charger les chambres au démarrage
  useEffect(() => {
    fetchRooms();
  }, [businessId]);

  // 2. Charger les réservations quand on change de mois ou de chambre
  useEffect(() => {
    if (selectedRoom) fetchMonthBookings();
  }, [currentDate, selectedRoom]);

  const fetchRooms = async () => {
    setLoading(true);
    const { data } = await supabase.from('accommodations').select('*').eq('business_id', businessId);
    if (data && data.length > 0) {
        setRooms(data);
        setSelectedRoom(data[0]); // Sélectionne la première chambre par défaut
    }
    setLoading(false);
  };

  const fetchMonthBookings = async () => {
    if (!selectedRoom) return;

    // Calcul du premier et dernier jour du mois affiché (avec marge)
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1).toISOString();
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0).toISOString();

    const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('accommodation_id', selectedRoom.id)
        .neq('status', 'cancelled')
        .or('status.eq.confirmed,status.eq.blocked')
        .gte('check_out_date', start)
        .lte('check_in_date', end);

    if (error) console.error("Erreur chargement bookings:", error);
    setBookings(data || []);
  };

  // --- FONCTION DE BLOCAGE ---
  const handleBlockDate = async (day: number) => {
    if (!selectedRoom) return alert("Aucune chambre sélectionnée.");
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // On construit la date à 12h00 pour éviter les problèmes de fuseau horaire
    const checkIn = new Date(year, month, day, 12, 0, 0);
    const checkOut = new Date(year, month, day + 1, 11, 0, 0); // Le lendemain

    // Récupération de l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return alert("Erreur : Vous devez être connecté.");

    // Insertion
    const { error } = await supabase.from('bookings').insert({
        business_id: businessId,
        accommodation_id: selectedRoom.id,
        user_id: user.id,
        check_in_date: checkIn.toISOString(),
        check_out_date: checkOut.toISOString(),
        status: 'blocked',        // IMPORTANT : Statut bloqué
        payment_status: 'none',   // Pas de paiement
        total_price: 0,
        guest_name: 'Fermé (Manuel)',
        guest_email: user.email
    });

    if (error) {
        alert("Erreur lors du blocage : " + error.message);
    } else {
        fetchMonthBookings();
        if (onUpdate) onUpdate();
    }
  };

  // --- FONCTION DE DÉBLOCAGE ---
  const handleUnblock = async (bookingId: string) => {
    if (confirm("Libérer cette date et la rendre disponible à la réservation ?")) {
        const { error } = await supabase.from('bookings').delete().eq('id', bookingId);
        
        if (error) {
            alert("Erreur : " + error.message);
        } else {
            fetchMonthBookings();
            if (onUpdate) onUpdate();
        }
    }
  };

  // --- CHANGEMENT DE MOIS ---
  const changeMonth = (delta: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  // --- RENDU DU CALENDRIER ---
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay(); // 0 = Dimanche

  const renderDays = () => {
    const days = [];
    
    // Cases vides avant le 1er du mois
    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} className="h-24 md:h-32 bg-slate-50/50 border border-slate-100"></div>);
    }

    // Les jours du mois
    for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), d, 12, 0, 0);
        
        // Chercher si une réservation existe pour ce jour
        // Amélioration : Vérification par timestamp pour plus de précision
        const booking = bookings.find(b => {
            const start = new Date(b.check_in_date);
            const end = new Date(b.check_out_date);
            return dateObj >= start && dateObj < end;
        });

        const isBooked = booking?.status === 'confirmed';
        const isBlocked = booking?.status === 'blocked';

        days.push(
            <div key={d} className={`h-24 md:h-32 border border-slate-100 p-2 relative group transition-all ${booking ? (isBlocked ? 'bg-slate-100' : 'bg-blue-50') : 'bg-white hover:bg-slate-50'}`}>
                <span className={`text-xs font-bold ${isBooked ? 'text-blue-600' : 'text-slate-900'}`}>{d}</span>
                
                {booking ? (
                    <div className="mt-2 animate-in fade-in flex flex-col items-center justify-center h-full pb-4">
                        {isBlocked ? (
                            <div onClick={() => handleUnblock(booking.id)} className="cursor-pointer bg-slate-200 hover:bg-red-100 hover:text-red-600 text-slate-600 text-[10px] p-1.5 rounded font-bold flex items-center gap-1 transition-colors w-full justify-center">
                                <Lock size={12}/> Fermé
                            </div>
                        ) : (
                            <div className="bg-blue-100 text-blue-700 text-[10px] p-1.5 rounded font-bold flex flex-col items-center gap-1 w-full text-center border border-blue-200">
                                <User size={12}/> 
                                <span className="truncate w-full">{booking.guest_name ? booking.guest_name.split(' ')[0] : 'Client'}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    // BOUTON BLOQUER (Apparaît au survol)
                    <button 
                        onClick={() => handleBlockDate(d)} 
                        className="absolute inset-0 w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 bg-slate-900/5 transition duration-200"
                    >
                        <span className="bg-white text-slate-900 text-[10px] font-black uppercase px-2 py-1 rounded shadow-sm border border-slate-200 hover:bg-slate-900 hover:text-white transition">
                            Bloquer
                        </span>
                    </button>
                )}
            </div>
        );
    }
    return days;
  };

  if (loading) return <div className="p-12 text-center text-slate-400 bg-slate-50 rounded-2xl animate-pulse">Chargement du calendrier...</div>;
  if (rooms.length === 0) return <div className="p-6 bg-yellow-50 text-yellow-800 rounded-2xl border border-yellow-100 text-sm font-bold text-center">⚠️ Vous devez d'abord ajouter une chambre dans l'onglet "Hébergement".</div>;

  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
        {/* En-tête Calendrier */}
        <div className="p-4 flex flex-col md:flex-row justify-between items-center gap-4 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3 w-full md:w-auto">
                <select 
                    value={selectedRoom?.id} 
                    onChange={(e) => setSelectedRoom(rooms.find(r => r.id === e.target.value))} 
                    className="bg-slate-50 font-bold text-sm px-4 py-2 rounded-xl outline-none cursor-pointer border border-slate-200 hover:border-slate-300 transition w-full md:w-auto"
                >
                    {rooms.map(r => <option key={r.id} value={r.id}>{r.name} ({r.price_per_night}$)</option>)}
                </select>
                <div className="flex gap-2 text-[10px] font-bold uppercase tracking-wider hidden md:flex">
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-full"><div className="w-2 h-2 bg-blue-500 rounded-full"></div> Réservé</span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded-full"><div className="w-2 h-2 bg-slate-400 rounded-full"></div> Bloqué</span>
                </div>
            </div>
            
            <div className="flex items-center justify-between w-full md:w-auto gap-4 bg-slate-50 p-1 rounded-xl">
                <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-white rounded-lg text-slate-500 transition shadow-sm"><ChevronLeft size={16}/></button>
                <h3 className="font-black text-sm capitalize text-slate-800 w-32 text-center">{currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</h3>
                <button onClick={() => changeMonth(1)} className="p-2 hover:bg-white rounded-lg text-slate-500 transition shadow-sm"><ChevronRight size={16}/></button>
            </div>
        </div>

        {/* Grille Jours Semaine */}
        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50">
            {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(d => (
                <div key={d} className="py-3 text-center text-[10px] font-black uppercase text-slate-400 tracking-widest">{d}</div>
            ))}
        </div>

        {/* Grille Calendrier */}
        <div className="grid grid-cols-7">
            {renderDays()}
        </div>

        {/* Footer Actions (Sync IA) */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-slate-400 flex items-center gap-1">
                <AlertCircle size={12}/> Cliquez sur une case libre pour bloquer la date manuellement.
            </p>
            
            {onAiSync && (
                <button 
                    onClick={onAiSync} 
                    disabled={aiLoading}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl font-bold uppercase text-[10px] tracking-widest hover:bg-indigo-700 transition disabled:opacity-70 shadow-sm"
                >
                    <RefreshCw size={14} className={aiLoading ? 'animate-spin' : ''} />
                    {aiLoading ? 'Analyse IA...' : 'Sync Calendriers (IA)'}
                </button>
            )}
        </div>
    </div>
  );
};

export default AvailabilityCalendar;
